import { createSlice, createSelector, PayloadAction } from "@reduxjs/toolkit";
import {
    getShiftsByDate,
    getSwapEmployees,
    updateSwapRequest,
    deleteSwapRequest,
    getSwapEmployeesV2,
    getMySchedules,
    getMySchedulesV2,
    getMyScheduleDetails,
    getMyTodaysSchedule,
    getShiftsByDateV2,
    releaseOpenShift,
    getTimecardApprovalDetails,
} from "./ScheduleApi";
import { FULFILLED, PENDING, REJECTED } from "../../constants";

import { IShift } from "./models/schedule";
import { SwappableEmployee } from "../../../types/state/swap";
import { GetMySchedulesResponse, GetMySchedulesV2Response, ScheduleStateType, ScheduleV2, ScheduleTimeoff, Unavailability, ScheduleV2Timeoff, ScheduleV2Holiday } from "../../../types";
// import { IShift, IAvailShifts } from './models/schedule'

const initialState: ScheduleStateType = {
    requestState: FULFILLED,
    shifts: [],
    selectedShift: null,
    dateRange: null,
    availShifts: null,
    availEmp: [],
    txnsForManager: [],
    txnsForUser: [],
    /** trade swap */
    createSwapReason: "",
    swappableEmployees: [],
    swapFilterStartDate: "",
    swapFilterEndDate: "",
    selectedSwapEmployees: [],
    swapNote: "",
    actionConsecutiveDays: 1,
    isLoadingSchedules: false,
    schedules: [],
    schedulesMap: {},

    // timeoff
    timeoffRequests: [],
    timeoffRequestsMap: {},

    isLoadingTodaysSchedule: false,

    // V2 specific state
    isLoadingSchedulesV2: false,
    schedulesV2: [],
    schedulesV2Map: {},
    timeoffRequestsV2: [],
    timeoffRequestsV2Map: {},
    unavailabilityRequestsV2: [],
    unavailabilityRequestsV2Map: {},
    holidaysV2: [],
    holidaysV2Map: {},
    isLoadingApprovalDetails: false,
    approvalDetails: undefined,
};

export const ScheduleSlice = createSlice({
    name: "schedule",
    initialState,
    reducers: {
        selectShift: (state, data) => {
            const shiftId = data.payload;
            const shift: any = state.shifts.find((shift) => shift.id.toString() === shiftId);
            state.selectedShift = shift;
        },
        setDateRange: (state, data) => {
            state.dateRange = data.payload;
        },
        tgShift: (state, data) => {
            let payload = data.payload;
            let idx = state.txnsForUser.findIndex((txn: any) => txn.id === payload);

            if (idx === -1) {
                return;
            } else {
                state.txnsForUser[idx].checked = true;
            }
        },
        clearTxns: (state) => {
            state.txnsForUser = [];
        },
        updateSelectedSwapEmployees: (state, data) => {
            state.selectedSwapEmployees = data.payload;
        },
        setFilterStartDate: (state, data) => {
            state.swapFilterStartDate = data.payload;
        },
        setFilterEndDate: (state, data) => {
            state.swapFilterEndDate = data.payload;
        },
        setSwapNote: (state, data) => {
            state.swapNote = data.payload;
        },

        setActionConsecutiveDays: (state, action: PayloadAction<number>) => {
            state.actionConsecutiveDays = action.payload;
        },

        setSelectedScheduleForCannotWork: (state, action: PayloadAction<ScheduleV2 | undefined>) => {
            state.selectedScheduleForCannotWork = action.payload;
        },

        clearSelectedScheduleForCannotWork: (state) => {
            state.selectedScheduleForCannotWork = undefined;
        },

        // Timeoff actions
        setSelectedTimeoffRequest: (state, action: PayloadAction<ScheduleTimeoff | undefined>) => {
            state.selectedTimeoffRequest = action.payload;
        },

        clearSelectedTimeoffRequest: (state) => {
            state.selectedTimeoffRequest = undefined;
        },
    },
    extraReducers: {
        /** NEW API REDUCERS */
        [getSwapEmployeesV2.pending.type]: (state, action) => {
            state.requestState = PENDING;
        },

        [getSwapEmployeesV2.fulfilled.type]: (state, { payload }) => {
            if (payload) {
                state.swappableEmployees = [...payload];
            }
            state.requestState = FULFILLED;
        },

        [getSwapEmployeesV2.rejected.type]: (state, action) => {
            state.requestState = REJECTED;
        },

        [getMySchedules.pending.type]: (state) => {
            state.isLoadingSchedules = true;
            state.schedules = [];
            state.schedulesMap = {};
            state.timeoffRequests = [];
            state.timeoffRequestsMap = {};
        },

        [getMySchedules.rejected.type]: (state) => {
            state.isLoadingSchedules = false;
        },

        [getMySchedules.fulfilled.type]: (state, action: PayloadAction<GetMySchedulesResponse>) => {
            state.isLoadingSchedules = false;
            const schedules = action.payload.schedules || [];
            const timeoffRequests = action.payload.timeoff || [];
            const unavailabilityRequests = action.payload.unavailability || [];

            state.schedules = schedules;
            state.timeoffRequests = timeoffRequests;
            state.unavailabilityRequests = unavailabilityRequests;

            state.schedulesMap = schedules.reduce((acc, schedule) => {
                acc[schedule.shift_date!] = acc[schedule.shift_date!] || [];
                acc[schedule.shift_date!].push(schedule);
                return acc;
            }, state.schedulesMap);

            state.timeoffRequestsMap = timeoffRequests.reduce((acc: { [key: string]: ScheduleTimeoff[] }, timeoff: ScheduleTimeoff) => {
                const dateKey = timeoff.start_date || '';
                if (dateKey && dateKey !== '1970-01-01') {
                    acc[dateKey] = acc[dateKey] || [];
                    acc[dateKey].push(timeoff);
                }
                return acc;
            }, state.timeoffRequestsMap || {});

            state.unavailabilityRequestsMap = unavailabilityRequests.reduce((acc: { [key: string]: Unavailability[] }, unavailability: Unavailability) => {
                const dateKey = unavailability.start_date || '';
                if (dateKey && dateKey !== '1970-01-01') {
                    acc[dateKey] = acc[dateKey] || [];
                    acc[dateKey].push(unavailability);
                }
                return acc;
            }, state.unavailabilityRequestsMap || {});
        },

        [getMySchedulesV2.pending.type]: (state) => {
            state.isLoadingSchedulesV2 = true;
            state.schedulesV2 = [];
            state.schedulesV2Map = {};
            state.timeoffRequestsV2 = [];
            state.timeoffRequestsV2Map = {};
            state.unavailabilityRequestsV2 = [];
            state.unavailabilityRequestsV2Map = {};
            state.holidaysV2 = [];
            state.holidaysV2Map = {};
        },

        [getMySchedulesV2.rejected.type]: (state) => {
            state.isLoadingSchedulesV2 = false;
        },

        [getMySchedulesV2.fulfilled.type]: (state, action: PayloadAction<GetMySchedulesV2Response>) => {
            state.isLoadingSchedulesV2 = false;
            const schedules = action.payload.schedules || [];
            const timeoffRequests = action.payload.timeoff || [];
            const unavailabilityRequests = action.payload.unavailability || [];
            const holidays = action.payload.holidays || [];

            // Debug logging
            console.log('Redux Debug - Raw timeoff data:', timeoffRequests);
            console.log('Redux Debug - Raw unavailability data:', unavailabilityRequests);
            console.log('Redux Debug - Raw holidays data:', holidays);

            state.schedulesV2 = schedules;
            state.timeoffRequestsV2 = timeoffRequests;
            state.unavailabilityRequestsV2 = unavailabilityRequests;
            state.holidaysV2 = holidays;

            // Clear old maps and build new ones
            state.schedulesV2Map = {};
            state.timeoffRequestsV2Map = {};
            state.unavailabilityRequestsV2Map = {};
            state.holidaysV2Map = {};

            state.schedulesV2Map = schedules.reduce((acc: { [key: string]: ScheduleV2[] }, schedule: ScheduleV2) => {
                acc[schedule.shift_date!] = acc[schedule.shift_date!] || [];
                acc[schedule.shift_date!].push(schedule);
                return acc;
            }, {});

            state.timeoffRequestsV2Map = timeoffRequests.reduce((acc: { [key: string]: ScheduleV2Timeoff[] }, timeoff: ScheduleV2Timeoff) => {
                const dateKey = timeoff.start_datetime ? timeoff.start_datetime.split(' ')[0] : '';
                if (dateKey && dateKey !== '1970-01-01') {
                    acc[dateKey] = acc[dateKey] || [];
                    acc[dateKey].push(timeoff);
                }
                return acc;
            }, {});

            state.unavailabilityRequestsV2Map = unavailabilityRequests.reduce((acc: { [key: string]: Unavailability[] }, unavailability: Unavailability) => {
                const dateKey = unavailability.start_date || '';
                if (dateKey && dateKey !== '1970-01-01') {
                    acc[dateKey] = acc[dateKey] || [];
                    acc[dateKey].push(unavailability);
                }
                return acc;
            }, {});

            state.holidaysV2Map = holidays.reduce((acc: { [key: string]: ScheduleV2Holiday[] }, holiday: ScheduleV2Holiday) => {
                const dateKey = holiday.start_date ? new Date(holiday.start_date).toISOString().split('T')[0] : '';
                if (dateKey && dateKey !== '1970-01-01') {
                    acc[dateKey] = acc[dateKey] || [];
                    acc[dateKey].push(holiday);
                }
                return acc;
            }, {});
        },

        [getMyTodaysSchedule.pending.type]: (state) => {
            state.isLoadingTodaysSchedule = true;
        },

        [getMyTodaysSchedule.rejected.type]: (state) => {
            state.isLoadingTodaysSchedule = false;
        },

        [getMyTodaysSchedule.fulfilled.type]: (state, action: PayloadAction<ScheduleV2 | undefined>) => {
            state.isLoadingTodaysSchedule = false;
            state.todaysSchedule = action.payload;
        },

        /** Get shifts by date reducers */
        [getShiftsByDate.pending.type]: (state, action) => {
            state.requestState = PENDING;
        },
        [getShiftsByDate.fulfilled.type]: (state, { payload }) => {
            if (payload) {
                state.shifts = [...payload.data];
            }
            state.requestState = FULFILLED;
        },
        [getShiftsByDate.rejected.type]: (state, action) => {
            state.requestState = REJECTED;
        },

        [getShiftsByDateV2.pending.type]: (state, action) => {
            state.requestState = PENDING;
        },
        [getShiftsByDateV2.fulfilled.type]: (state, { payload }) => {
            if (payload) {
                state.shifts = [...payload];
            }
            state.requestState = FULFILLED;
        },
        [getShiftsByDateV2.rejected.type]: (state, action) => {
            state.requestState = REJECTED;
        },

        /** get swap employees recuders */
        [getSwapEmployees.pending.type]: (state, action) => {
            state.requestState = PENDING;
        },
        [getSwapEmployees.fulfilled.type]: (state, { payload }) => {
            if (payload) {
                state.swappableEmployees = [...payload.data];
            }
            state.requestState = FULFILLED;
        },
        [getSwapEmployees.rejected.type]: (state, action) => {
            state.requestState = REJECTED;
        },

        /** update swap request reducers */
        [updateSwapRequest.pending.type]: (state, action) => {
            state.requestState = PENDING;
        },

        [updateSwapRequest.fulfilled.type]: (state, { payload }) => {
            state.requestState = FULFILLED;
        },

        [updateSwapRequest.rejected.type]: (state, action) => {
            state.requestState = REJECTED;
        },

        [deleteSwapRequest.pending.type]: (state, action) => {
            state.requestState = PENDING;
        },

        [deleteSwapRequest.fulfilled.type]: (state, { payload }) => {
            state.requestState = FULFILLED;
        },

        [deleteSwapRequest.rejected.type]: (state, action) => {
            state.requestState = REJECTED;
        },

        [getMyScheduleDetails.pending.type]: (state) => {
            state.isScheduleDetailLoading = true;
            state.scheduleDetail = undefined;
        },

        [getMyScheduleDetails.fulfilled.type]: (state, action: PayloadAction<ScheduleV2>) => {
            state.isScheduleDetailLoading = false;
            // state.scheduleDetail = action.payload;
            state.scheduleDetail = {
                ...action.payload,
                // action_button: {
                //     can_swap: true,
                //     can_release: true,
                // },
            };
        },

        [getMyScheduleDetails.rejected.type]: (state) => {
            state.isScheduleDetailLoading = false;
        },

        /** Release open shift reducers */
        [releaseOpenShift.pending.type]: (state, action) => {
            state.requestState = PENDING;
        },

        [releaseOpenShift.fulfilled.type]: (state, action) => {
            state.requestState = FULFILLED;
        },

        [releaseOpenShift.rejected.type]: (state, action) => {
            state.requestState = REJECTED;
        },

        /** Timecard approval details */
        [getTimecardApprovalDetails.pending.type]: (state) => {
            state.isLoadingApprovalDetails = true;
            state.approvalDetails = undefined;
        },
        [getTimecardApprovalDetails.fulfilled.type]: (state, action) => {
            state.isLoadingApprovalDetails = false;
            state.approvalDetails = action.payload;
        },
        [getTimecardApprovalDetails.rejected.type]: (state) => {
            state.isLoadingApprovalDetails = false;
        },
    },
});

export const selectShifts = createSelector(
    (state: any) => state.shifts,
    (shifts: any) => shifts
);

export const {
    selectShift,
    setDateRange,
    tgShift,
    clearTxns,
    updateSelectedSwapEmployees,
    setFilterStartDate,
    setFilterEndDate,
    setSwapNote,
    setActionConsecutiveDays,
    clearSelectedScheduleForCannotWork,
    setSelectedScheduleForCannotWork,
    setSelectedTimeoffRequest,
    clearSelectedTimeoffRequest,
} = ScheduleSlice.actions;

export default ScheduleSlice.reducer;
